import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import {
  ConnectorAccessPolicy,
  ConnectorContractPolicy
} from '../../connector-control-plane.component';
import { OrganisationMinimalInterface } from 'src/app/model/organisations/organisation-minimal-interface';
import { DataOfferPricingDto } from '../../../../models/bautagebuch.models';

@Component({
  standalone: false,
  selector: 'app-policy-editor',
  templateUrl: './policy-editor.component.html',
  styleUrls: ['./policy-editor.component.scss']
})
export class PolicyEditorComponent implements OnChanges {
  @Input() accessPolicy!: ConnectorAccessPolicy;
  @Input() contractPolicy!: ConnectorContractPolicy;
  @Input() pricing!: DataOfferPricingDto;
  @Input() availableOrganisations: OrganisationMinimalInterface[] = [];
  @Output() policiesChange = new EventEmitter<{
    accessPolicy: ConnectorAccessPolicy;
    contractPolicy: ConnectorContractPolicy;
    pricing: DataOfferPricingDto;
  }>();

  showJsonView = false;
  customTermsInput = '';
  customTermsError = '';
  orgSearchTerm = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.contractPolicy) {
      this.customTermsInput = this.formatCustomTerms(this.contractPolicy?.customTerms);
      this.customTermsError = '';
    }
  }

  get exportAllowed(): boolean {
    return this.contractPolicy.mode === 'controlled-export';
  }

  get showPaidFields(): boolean {
    return this.pricing?.type === 'PAID';
  }

  get currencyOptions(): string[] {
    return ['EUR', 'USD', 'CHF'];
  }

  get billingBasisOptions(): string[] {
    return ['PER_REQUEST', 'PER_MONTH', 'PER_MB'];
  }

  get filteredOrganisations(): OrganisationMinimalInterface[] {
    if (!this.orgSearchTerm.trim()) {
      return this.availableOrganisations;
    }
    const term = this.orgSearchTerm.trim().toLowerCase();
    return this.availableOrganisations.filter((org) =>
      (org.name || '').toLowerCase().includes(term)
    );
  }

  get policyAsJson(): { [key: string]: any } {
    const consumerOrgId =
      this.accessPolicy.visibility === 'restricted' && this.accessPolicy.consumerOrgIds.length
        ? this.accessPolicy.consumerOrgIds[0]
        : 0;
    return {
      consumerOrgId,
      allowedPurpose: 'BAUTAGEBUCH_CREATION',
      terms: this.buildPolicyTerms(),
      pricing: this.buildPricingJson()
    };
  }

  private buildPricingJson(): { [key: string]: any } {
    if (!this.pricing || this.pricing.type === 'FREE') {
      return { type: 'FREE' };
    }
    return {
      type: this.pricing.type,
      amount: this.pricing.amount,
      currency: this.pricing.currency,
      billingBasis: this.pricing.billingBasis
    };
  }

  updateVisibility(visibility: ConnectorAccessPolicy['visibility']): void {
    const consumerOrgIds = visibility === 'restricted' ? this.accessPolicy.consumerOrgIds : [];
    const catalogAudienceLabel = this.buildCatalogAudienceLabel(visibility, consumerOrgIds);

    this.emit({
      accessPolicy: {
        ...this.accessPolicy,
        visibility,
        catalogAudienceLabel,
        consumerOrgIds
      }
    });
  }

  updateConsumerOrgs(consumerOrgIds: number[]): void {
    const catalogAudienceLabel = this.buildCatalogAudienceLabel('restricted', consumerOrgIds);
    this.emit({
      accessPolicy: {
        ...this.accessPolicy,
        visibility: 'restricted',
        consumerOrgIds,
        catalogAudienceLabel
      }
    });
  }

  updateMode(mode: ConnectorContractPolicy['mode']): void {
    const exportFormats =
      mode === 'controlled-export'
        ? this.contractPolicy.exportFormats.length
          ? this.contractPolicy.exportFormats
          : ['PDF']
        : [];
    const permissions: Array<'VIEW' | 'EXPORT_PDF' | 'EXPORT_XML' | 'COMPUTE_TO_DATA'> =
      mode === 'compute-to-data'
        ? ['COMPUTE_TO_DATA']
        : mode === 'controlled-export'
          ? this.normalizePermissions(
              ['VIEW', ...exportFormats.map((format) => this.mapExportFormatToPermission(format))]
            )
          : ['VIEW'];

    this.emit({
      contractPolicy: {
        ...this.contractPolicy,
        mode,
        permissions,
        exportFormats
      }
    });
  }

  toggleExportFormat(format: string): void {
    const exportFormats = new Set(this.contractPolicy.exportFormats || []);
    if (exportFormats.has(format)) {
      exportFormats.delete(format);
    } else {
      exportFormats.add(format);
    }

    this.emit({
      contractPolicy: {
        ...this.contractPolicy,
        permissions: this.normalizePermissions([
          'VIEW',
          ...Array.from(exportFormats).map((selectedFormat) =>
            this.mapExportFormatToPermission(selectedFormat)
          )
        ]),
        exportFormats: Array.from(exportFormats)
      }
    });
  }

  isFormatSelected(format: string): boolean {
    return (this.contractPolicy.exportFormats || []).includes(format);
  }

  updateCustomTerms(rawJson: string): void {
    this.customTermsInput = rawJson;
    if (!rawJson.trim()) {
      this.customTermsError = '';
      this.emit({
        contractPolicy: {
          ...this.contractPolicy,
          customTerms: {}
        }
      });
      return;
    }

    try {
      const parsed = JSON.parse(rawJson);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        this.customTermsError = '';
        this.emit({
          contractPolicy: {
            ...this.contractPolicy,
            customTerms: parsed
          }
        });
      } else {
        this.customTermsError = 'JSON muss ein Objekt sein.';
      }
    } catch {
      this.customTermsError = 'Ungültiges JSON.';
    }
  }

  updatePricingType(type: 'FREE' | 'PAID'): void {
    this.emit({
      pricing:
        type === 'FREE'
          ? { type: 'FREE', amount: null, currency: null, billingBasis: null }
          : {
              type: 'PAID',
              amount: this.pricing?.amount || 49,
              currency: this.pricing?.currency || 'EUR',
              billingBasis: this.pricing?.billingBasis || 'PER_REQUEST'
            }
    });
  }

  updatePricingAmount(amount: number): void {
    this.emit({
      pricing: {
        ...this.pricing,
        type: 'PAID',
        amount: amount === null || amount === undefined ? null : Number(amount)
      }
    });
  }

  updatePricingCurrency(currency: string): void {
    this.emit({
      pricing: {
        ...this.pricing,
        type: 'PAID',
        currency
      }
    });
  }

  updateBillingBasis(billingBasis: string): void {
    this.emit({
      pricing: {
        ...this.pricing,
        type: 'PAID',
        billingBasis
      }
    });
  }

  private emit(
    patch: Partial<{
      accessPolicy: ConnectorAccessPolicy;
      contractPolicy: ConnectorContractPolicy;
      pricing: DataOfferPricingDto;
    }>
  ): void {
    this.policiesChange.emit({
      accessPolicy: patch.accessPolicy || this.accessPolicy,
      contractPolicy: patch.contractPolicy || this.contractPolicy,
      pricing: patch.pricing || this.pricing
    });
  }

  private buildPolicyTerms(): { [key: string]: any } {
    const baseTerms: { [key: string]: any } = {
      mode: this.contractPolicy.mode,
      usagePermissions: this.contractPolicy.permissions,
      exportFormats: this.contractPolicy.exportFormats
    };
    if (this.contractPolicy.customTerms && Object.keys(this.contractPolicy.customTerms).length) {
      return { ...baseTerms, customTerms: this.contractPolicy.customTerms };
    }
    return baseTerms;
  }

  private buildCatalogAudienceLabel(
    visibility: ConnectorAccessPolicy['visibility'],
    consumerOrgIds: number[]
  ): string {
    if (visibility === 'restricted') {
      if (consumerOrgIds.length) {
        return `Nur für ${consumerOrgIds.length} Partner sichtbar`;
      }
      return 'Nur für ausgewählte Partner sichtbar';
    }
    return 'Sichtbar für alle GU im Federated Catalog';
  }

  private formatCustomTerms(terms: { [key: string]: any } | null | undefined): string {
    if (!terms || !Object.keys(terms).length) {
      return '';
    }
    try {
      return JSON.stringify(terms, null, 2);
    } catch {
      return '';
    }
  }

  private normalizePermissions(
    permissions: Array<'VIEW' | 'EXPORT_PDF' | 'EXPORT_XML' | 'COMPUTE_TO_DATA'>
  ): Array<'VIEW' | 'EXPORT_PDF' | 'EXPORT_XML' | 'COMPUTE_TO_DATA'> {
    const unique = Array.from(new Set(permissions));
    if (unique.includes('EXPORT_PDF') || unique.includes('EXPORT_XML')) {
      return unique.includes('VIEW') ? unique : [...unique, 'VIEW'];
    }
    return unique;
  }

  private mapExportFormatToPermission(
    format: string
  ): 'EXPORT_PDF' | 'EXPORT_XML' {
    return format === 'XML' ? 'EXPORT_XML' : 'EXPORT_PDF';
  }
}
